import React, { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useDispatch, useSelector } from 'react-redux'
import { Fade } from 'react-reveal'
import DashedButton from '../../../components/elements/Buttons/DashedButton'
import FormDomain from '../../../components/elements/forms/FormDomain'
import { EmptyMessage } from '../../../components/EmptyMessage'
import SuccessDialog from '../../../components/SuccessDialog'
import WebsiteTab from '../../../components/WebsiteTab'
import ToastAction from '../../../components/ToastAction'
import { closeMessageConcurrent, createConcurrent, deleteConcurrent, fetchConcurrent, getConcurrents, updateConcurrent } from '../../../features/concurrents/concurrentsSlice'
import { selectUserInfo } from '../../../features/users/userInfoSlice'
import { fetchWebsites, selectAll } from '../../../features/websites/websitesSlice'
import { useSubscriptionGard } from '../../../guards/subscription/useSubscriptionGard'
import { useHistory } from 'react-router-dom'
import { classNames, getPluralWord } from '../../../utils'
import { flushSync } from 'react-dom'
import { selectorTour } from '../../../features/tour/tourSlice'

function Concurrents() {
  const history = useHistory()
  const dispatch = useDispatch();
  const listRef = useRef()
  const [addNew, setAddNew] = useState(false)
  const showMessage = useSelector(state => state.concurrents.showMessage)
  const userInfoContext = useSelector(selectUserInfo)
  const websites = useSelector(selectAll)
  const errorConcurrents = useSelector(state => state.concurrents.error)
  const concurrents = useSelector(getConcurrents)
  const loadingConcurrent = useSelector(state => state.concurrents.loading)
  const activeWebsite = useSelector(state => state.websites.activeWebsite)
  const isTourLaunched = useSelector(selectorTour)
  
  const { competitors } = useSubscriptionGard()


  useEffect(() => {
    if (websites.length > 0) return
    dispatch(fetchWebsites({ company_id: userInfoContext?.user?.company_id }))
  }, [userInfoContext?.user?.company_id])

  useEffect(() => {
    if (activeWebsite) dispatch(fetchConcurrent({ parent_website_id: activeWebsite?.id }))
  }, [activeWebsite])

  useEffect(() => {
    if (!loadingConcurrent && !errorConcurrents.status) setAddNew(false)
  }, [loadingConcurrent, errorConcurrents])

  const _add = useCallback(() => {
    if (!competitors.canAdd) {
      const label = competitors.limit
        ? `Votre forfait actuel ne vous permet pas de rajouter plus de ${competitors.limit} ${getPluralWord('concurrent', competitors.limit)} par site.`
        : "Vous n'avez pas encore souscrit à un forfait."
      const description = competitors.limit ? "Voulez-vous changer de forfait ?" : "Voulez-vous souscrire maintenant ?"

      toast.custom((t) => <ToastAction saraExpression="wink" toastRef={t} label={label} description={description} okAction={() => history.push('/account/forfait')} />
      )
      return
    }
    flushSync(() => {
      setAddNew(true)
    })

    concurrents.length >= 4 && listRef.current?.lastElementChild?.scrollIntoView()
  }, [competitors.canAdd, listRef.current, concurrents])

  const _saveConcurrent = useCallback((concurrent) => {
    concurrent.website_parent_id = activeWebsite?.id
    concurrent.company_id = userInfoContext?.user?.company_id

    if (concurrent.id) dispatch(updateConcurrent({ concurrent }))
    else {
      if (!competitors.canAdd) return
      if (!!concurrents.find(con => con.site_url?.trim() === concurrent.site_url?.trim())) {
        return toast.custom((t) => <ToastAction saraExpression="wink" toastRef={t} label="Ce concurrent existe déjà !" description="Vous ne pouvez pas enregistrer deux concurrents avec la même URL." />
        )
      }
      delete concurrent?.website_id
      dispatch(createConcurrent({ concurrent }))
    }
  }, [activeWebsite, concurrents, competitors.canAdd])

  const _deleteConcurrent = useCallback((id) => {
    if (id) dispatch(deleteConcurrent({ website_id: id, parent_website_id: activeWebsite?.id, company_id: userInfoContext?.user?.company_id }))
    else setAddNew(false)
  }, [activeWebsite, userInfoContext?.user?.company_id])

  const _remove = useCallback(() => {
    setAddNew(false)
  }, [])

  const _closeMessageDialog = useCallback(() => {
    dispatch(closeMessageConcurrent())
  }, [])

  const messageByRole = !userInfoContext?.user?.admin ? "Veuillez contacter l'administrateur de votre companie pour ajouter un site." : 'Vérifiez que vous avez enregistré au moins un site pour pouvoir ajouter ses concurrents.'

  return (
    <div className="max-w-4xl mx-auto space-y-3 mt-20 concurrent-step">
      <SuccessDialog open={showMessage} close={_closeMessageDialog} description="Votre concurrent a été crée avec succès" display={true} />
      <div className="space-y-8">
        <div className="max-w-lg mx-auto mt-10">
          <WebsiteTab />
        </div>
      </div>
      {
        activeWebsite &&
        <p className="text-gray-400 text-xs text-center font-semibold pb-10">
          Merci de remplir l'url du ou des concurrents que vous voulez suivre
        </p>
      }
      <section ref={listRef}
        className={
          classNames("max-h-[350px] overflow-y-auto rounded-2xl p-4 scroll-smooth space-y-2 overflow-x-hidden",
            concurrents.length >= 4 ? "bg-gray-100 shadow-inner" : "")}
      >
        {
          concurrents.length > 0 && concurrents.map((concurrent, _index) =>
            <Fade top key={concurrent.id}>
              <FormDomain
                type='concurrent'
                key={concurrent.id}
                site={concurrent}
                company_id={userInfoContext?.user?.company_id}
                saveSite={_saveConcurrent}
                deleteSite={_deleteConcurrent} />
            </Fade>
          )
        }
        {
          activeWebsite && addNew && <Fade>
            <FormDomain
              type='concurrent'
              site={{}}
              canAdd={competitors.canAdd}
              company_id={userInfoContext?.user?.company_id}
              saveSite={_saveConcurrent}
              deleteSite={_remove} />
          </Fade>
        }
      </section>
      {activeWebsite ? <DashedButton onClick={_add} disabled={isTourLaunched || addNew || loadingConcurrent || !activeWebsite} label="Ajouter un concurrent" /> : websites.length > 0 ? <EmptyMessage title='Selectionner un site !' message='Cliquez sur un site dans le menu au dessus pour ajouter ses concurrents.' /> :
        <EmptyMessage title="Vous n'avez enregistré aucun site !" message={messageByRole} />}
    </div>
  )
}

export default Concurrents